import React, {useState, useEffect, useContext} from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Chip,
  useMediaQuery,
  useTheme as useMuiTheme,
  Card,
  CardHeader,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Event,
  AccessTime,
  Sick,
  BeachAccess,
  WbCloudy,
  Close,
  Today,
  Construction,
} from '@mui/icons-material';
import APIWrapperContext from '../../../../services/APIWrapperContext';
import {useTheme} from '@mui/material/styles';
import dayjs from 'dayjs';
import UserContext from '../../../../Contexts/UserContext/UserContext';
import ZeiterfassungContext from '../../../../Contexts/ZeiterfassungContext/ZeiterfassungContext';
import {ArbeitszeitService, ApiError} from '../../../../services/openapi';

// Day entry interface
interface IDayEntries {
  [date: string]: Array<any>;
}

const UserCalendar = ({onClose}: {onClose: () => void}) => {
  const [calendarDate, setCalendarDate] = useState(dayjs());
  const [calendarData, setCalendarData] = useState<IDayEntries>({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const {call_backend_api} = useContext(APIWrapperContext);
  const {additionalUserdata} = useContext(UserContext);
  const {setSelectedDate} = useContext(ZeiterfassungContext);
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  // Get days in month
  const daysInMonth = calendarDate.daysInMonth();

  // Get the first day of the month
  const firstDayOfMonth = (calendarDate.startOf('month').day() + 6) % 7; // 0 = Sunday, 1 = Monday, ...

  // Function to fetch calendar data for the entire month at once
  const fetchCalendarData = async () => {
    setLoading(true);
    const year = calendarDate.year();
    const month = calendarDate.month() + 1; // dayjs months are 0-indexed

    try {
      // Use the new month endpoint to fetch all data for the month in one call
      call_backend_api(
        ArbeitszeitService.getWorktimesOfMonthArbeitzeitArbeitszeiteintragMonthGet(year, month),
        (response: any) => {
          // Group entries by date
          const dataObject: IDayEntries = {};

          // Initialize all days in the month with empty arrays
          for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${month.toString().padStart(2, '0')}-${day
              .toString()
              .padStart(2, '0')}`;
            dataObject[dateStr] = [];
          }

          // Fill in the days that have entries
          response.forEach((entry: any) => {
            // Use the datum field instead of extracting date from beginn
            // This ensures entries are displayed on the correct day
            if (entry.datum) {
              // Make sure the datum field is used for grouping entries
              dataObject[entry.datum].push(entry);
            } else {
              // Fallback to beginn if datum is not available
              const dateStr = dayjs(entry.beginn).format('YYYY-MM-DD');
              dataObject[dateStr].push(entry);
            }
          });

          setCalendarData(dataObject);
        },
        (error: ApiError) => {
          console.error(`Error fetching data for month ${month}/${year}:`, error);
          setCalendarData({});
        },
        undefined,
        false
      );
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [calendarDate]);

  // Navigate to previous month
  const handlePreviousMonth = () => {
    setCalendarDate(calendarDate.subtract(1, 'month'));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCalendarDate(calendarDate.add(1, 'month'));
  };

  // Navigate to current month
  const handleCurrentMonth = () => {
    setCalendarDate(dayjs());
  };

  // Open details dialog
  const handleDayClick = (date: string) => {
    if (!date) return;
    setSelectedDay(date);
    setDetailsOpen(true);
  };

  // Close details dialog
  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  // Set selected date and close calendar
  const handleSelectDate = (date: string) => {
    setSelectedDate(new Date(date));
    onClose();
  };

  // Format time from ISO string
  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    return dayjs(timeString).format('HH:mm');
  };

  // Format pause time from seconds
  const formatPauseTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Get icon for activity type
  const getActivityIcon = (activity?: string) => {
    switch (activity) {
      case 'Urlaub':
        return <BeachAccess color='primary' />;
      case 'Krank':
        return <Sick color='error' />;
      case 'Schlechtwetter':
        return <WbCloudy color='action' />;
      default:
        return <Construction color='success' />;
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const days = [];
    const today = dayjs();

    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<Grid item key={`empty-${i}`} xs={1}></Grid>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${calendarDate.year()}-${(calendarDate.month() + 1)
        .toString()
        .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const entries = calendarData[date] || [];
      const currentDate = dayjs(date);
      const isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
      const isToday = currentDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');

      // Check if this day is in the past
      const isPast = currentDate.isBefore(today, 'day');

      // Check if this is a weekday in the past with no entries
      // This identifies days the user "forgot" to add entries
      const isForgotten = !isWeekend && isPast && entries.length === 0;

      days.push(
        <Grid item key={day} xs={1}>
          <Card
            elevation={isToday ? 3 : 1}
            sx={{
              p: 0,
              height: {xs: '70px', sm: '90px', md: '120px'},
              cursor: 'pointer',
              backgroundColor: isForgotten
                ? 'rgba(244, 67, 54, 0.08)'
                : isToday
                ? 'rgba(25, 118, 210, 0.06)'
                : 'white',
              border: isToday
                ? '2px solid #1976d2'
                : isForgotten
                ? '1px solid rgba(244, 67, 54, 0.3)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              color: isForgotten ? 'error.main' : 'inherit',
              overflow: 'hidden',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3,
                backgroundColor: isForgotten
                  ? 'rgba(244, 67, 54, 0.12)'
                  : isToday
                  ? 'rgba(25, 118, 210, 0.1)'
                  : 'rgba(0, 0, 0, 0.03)',
              },
              position: 'relative',
            }}
            onClick={() => handleDayClick(date)}>
            {/* Day number header */}
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              sx={{
                py: 0.5,
                px: 1.5,
                backgroundColor: isWeekend ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              }}>
              <Typography
                variant='body2'
                fontWeight={isToday ? 'bold' : isWeekend ? 'normal' : 'medium'}
                color={isForgotten ? 'error.main' : isWeekend ? 'text.secondary' : 'text.primary'}>
                {day}
              </Typography>
              {/* Calendar badge removed as requested */}
            </Box>

            {/* Entry indicators */}
            <Box sx={{px: 1, py: 0.5, overflow: 'hidden'}}>
              {entries.slice(0, isMobile ? 1 : isMedium ? 1 : 2).map((entry, idx) => (
                <Box
                  key={idx}
                  display='flex'
                  alignItems='center'
                  gap={0.5}
                  sx={{
                    mb: 0.5,
                    backgroundColor:
                      entry.taetigkeit === 'Urlaub'
                        ? 'rgba(25, 118, 210, 0.08)'
                        : entry.taetigkeit === 'Krank'
                        ? 'rgba(244, 67, 54, 0.08)'
                        : entry.taetigkeit === 'Schlechtwetter'
                        ? 'rgba(237, 108, 2, 0.08)'
                        : 'rgba(76, 175, 80, 0.08)',
                    px: 0.75,
                    py: 0.25,
                  }}>
                  {getActivityIcon(entry.taetigkeit)}
                  <Typography
                    noWrap
                    variant='caption'
                    sx={{
                      fontSize: {xs: '0.6rem', sm: '0.65rem', md: '0.7rem'},
                      display: {xs: isMobile ? 'none' : 'block', sm: 'block'},
                    }}>
                    {entry.taetigkeit === 'Krank' ||
                    entry.taetigkeit === 'Urlaub' ||
                    entry.taetigkeit === 'Schlechtwetter'
                      ? 'Ganztägig'
                      : `${formatTime(entry.beginn)} - ${formatTime(entry.ende)}`}
                  </Typography>
                </Box>
              ))}
              {entries.length > (isMobile ? 1 : 2) && (
                <Typography
                  variant='caption'
                  color='primary'
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 0.5,
                    fontWeight: 'medium',
                    fontSize: '0.65rem',
                  }}>
                  +{entries.length - (isMobile ? 1 : 2)} mehr
                </Typography>
              )}
            </Box>

            {/* Empty weekday indicator dot */}
            {isForgotten && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 6,
                  bottom: 6,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                }}
              />
            )}
          </Card>
        </Grid>
      );
    }

    return days;
  };

  return (
    <Card>
      <CardHeader
        action={
          <Box
            display='flex'
            alignItems='center'
            sx={{
              borderRadius: 12,
              backgroundColor: 'background.paper',
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              width: {xs: '100%', sm: 'auto'},
              justifyContent: {xs: 'space-between', sm: 'flex-end'},
            }}>
            <IconButton onClick={handlePreviousMonth} size='small' sx={{color: 'text.secondary'}}>
              <ChevronLeft />
            </IconButton>

            <Button
              variant='contained'
              onClick={handleCurrentMonth}
              startIcon={<Today />}
              size='small'
              sx={{
                mx: 1,
                boxShadow: 1,
                textTransform: 'none',
                fontSize: {xs: '0.75rem', sm: '0.8rem', md: '0.875rem'},
                py: {xs: 0.5},
                px: {xs: 1, sm: 1.5},
              }}>
              Heute
            </Button>

            <IconButton onClick={handleNextMonth} size='small' sx={{color: 'text.secondary'}}>
              <ChevronRight />
            </IconButton>

            <IconButton
              onClick={onClose}
              sx={{
                ml: 1,
                border: '1px solid rgba(0,0,0,0.08)',
                color: 'text.secondary',
              }}
              size='small'>
              <Close fontSize='small' />
            </IconButton>
          </Box>
        }
        title={
          <Typography
            variant='h6'
            sx={{
              fontWeight: 'medium',
              textTransform: 'capitalize',
              fontSize: {xs: '1rem', sm: '1.1rem', md: '1.25rem'},
              color: 'white',
            }}>
            {calendarDate.locale('de').format('MMMM YYYY')}
          </Typography>
        }
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
        }}
      />
      <CardContent>
        {/* Calendar header - days of week */}
        <Grid container spacing={0.5} columns={7} sx={{mb: 0.5}}>
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
            <Grid item key={day} xs={1}>
              <Card
                sx={{
                  p: {xs: 0.5, sm: 0.75, md: 1},
                  textAlign: 'center',
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  fontWeight: 'medium',
                  border: '1px solid rgba(25, 118, 210, 0.15)',
                }}
                elevation={0}>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontSize: {xs: '0.6rem', sm: '0.7rem', md: '0.875rem'},
                    fontWeight: 600,
                  }}>
                  {day}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Calendar days */}
        <Grid container spacing={0.5} columns={7}>
          {loading ? (
            <Box sx={{p: 3, width: '100%', textAlign: 'center'}}>
              <Typography>Lädt Kalender...</Typography>
            </Box>
          ) : (
            generateCalendarDays()
          )}
        </Grid>
      </CardContent>

      {/* Day details dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            overflow: 'hidden',
            margin: isMobile ? '16px' : undefined,
            maxHeight: isMobile ? 'calc(100% - 32px)' : undefined,
          },
        }}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            py: 2,
            px: {xs: 2, sm: 3},
          }}>
          <Box display='flex' alignItems='center'>
            <CalendarMonth sx={{mr: 1}} />
            <Typography fontWeight='medium' sx={{fontSize: {xs: '0.9rem', sm: '1rem'}}}>
              {selectedDay ? dayjs(selectedDay).locale('de').format('DD. MMMM YYYY') : ''}
            </Typography>
          </Box>
          <Box>
            <Button
              variant='contained'
              onClick={() => selectedDay && handleSelectDate(selectedDay)}
              startIcon={isMobile ? null : <Today />}
              sx={{
                mr: 1,
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                },
                fontSize: {xs: '0.75rem', sm: '0.875rem'},
                py: {xs: 0.5, sm: 0.75},
                px: {xs: 1, sm: 1.5},
              }}
              size='small'>
              {isMobile ? 'Wählen' : 'Auswählen'}
            </Button>
            <IconButton edge='end' onClick={handleCloseDetails} sx={{color: 'white'}} size='small'>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{p: 0}}>
          {selectedDay && calendarData[selectedDay] && calendarData[selectedDay].length > 0 ? (
            <List sx={{px: 0, py: 1}}>
              {calendarData[selectedDay].map((entry, index) => {
                const getBgColor = () => {
                  switch (entry.taetigkeit) {
                    case 'Urlaub':
                      return 'rgba(25, 118, 210, 0.06)';
                    case 'Krank':
                      return 'rgba(244, 67, 54, 0.06)';
                    case 'Schlechtwetter':
                      return 'rgba(237, 108, 2, 0.06)';
                    default:
                      return 'rgba(76, 175, 80, 0.06)';
                  }
                };

                return (
                  <Card
                    key={index}
                    sx={{
                      mx: 2,
                      mb: 1,
                      backgroundColor: getBgColor(),
                      '&:last-child': {mb: 0},
                    }}
                    elevation={0}>
                    <CardContent sx={{p: 1.5}}>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        {getActivityIcon(entry.taetigkeit)}
                        <Box sx={{flex: 1}}>
                          <Typography variant='body2' sx={{fontWeight: 'medium'}}>
                            {entry.taetigkeit || 'Arbeit'}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {entry.taetigkeit === 'Krank' ||
                            entry.taetigkeit === 'Urlaub' ||
                            entry.taetigkeit === 'Schlechtwetter'
                              ? 'Ganztägig'
                              : `${formatTime(entry.beginn)} - ${formatTime(entry.ende)}${
                                  entry.pause > 0 ? ` (${formatPauseTime(entry.pause)} Pause)` : ''
                                }`}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </List>
          ) : (
            <Box
              sx={{
                p: {xs: 3, sm: 4},
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}>
              <Event sx={{fontSize: {xs: 36, sm: 48}, color: 'text.disabled'}} />
              <Typography
                color='text.secondary'
                variant='h6'
                sx={{fontSize: {xs: '1rem', sm: '1.25rem'}}}>
                Keine Einträge für diesen Tag
              </Typography>
              <Button
                variant='outlined'
                size='small'
                onClick={() => selectedDay && handleSelectDate(selectedDay)}
                startIcon={<Today />}>
                Tag auswählen
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{px: {xs: 2, sm: 3}, py: {xs: 1.5, sm: 2}}}>
          <Button onClick={handleCloseDetails} variant='contained' sx={{textTransform: 'none'}}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Calendar Button Component to open the calendar dialog
export const CalendarButton = () => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  // Check if screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        variant='outlined'
        startIcon={<CalendarMonth />}
        onClick={handleOpen}
        sx={{
          ml: {xs: 0, sm: 2},
          mt: {xs: 1, sm: 0},
          textTransform: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          borderColor: 'rgba(25, 118, 210, 0.5)',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
        }}
        fullWidth={isMobile}
        size={isMobile ? 'medium' : 'small'}>
        Kalender anzeigen
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='lg'
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            margin: isMobile ? 0 : 2,
            maxHeight: isMobile ? '100%' : 'calc(100% - 64px)',
            height: isMobile ? '100%' : 'auto',
          },
        }}>
        <DialogContent sx={{p: 0}}>
          <UserCalendar onClose={handleClose} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserCalendar;
